// src/hooks/useAnalyticsFetch.ts
//
// Shared {data, loading, error} fetch for the Dirección analytics pages
// (Resumen, Canales, Tiempos, Cobros, Instalaciones): each one used to catch
// a failed request into `setData(null)`, which renders identically to "no
// data in this period" — no banner, no retry (F-A-007). This hook keeps the
// exact same data-provider call and parameters; it only remembers whether
// the last attempt failed, so the screen can tell the two cases apart.
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseAnalyticsFetchResult<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
  refresh: () => void;
}

/**
 * @param fetcher Resolves the page's own data-provider call; recreate it on
 * every render (it closes over the current filters). Only the response of
 * the latest call is applied, so a slow, superseded request can never
 * overwrite a newer one.
 * @param deps Dependency list that should trigger a refetch — the same
 * values the page used to list in its own `useEffect`.
 * @param enabled When false no request is made (e.g. a v2 screen with no
 * property selected yet): `data` stays null and `loading` false.
 */
export const useAnalyticsFetch = <T,>(
  fetcher: () => Promise<{ data: T }>,
  deps: React.DependencyList,
  enabled = true
): UseAnalyticsFetchResult<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestId = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(() => {
    const id = ++requestId.current;
    if (!enabled) {
      setData(null);
      setError(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    fetcherRef
      .current()
      .then((res) => {
        if (id !== requestId.current) return;
        setData(res.data);
      })
      .catch(() => {
        if (id !== requestId.current) return;
        setError(true);
      })
      .finally(() => {
        if (id !== requestId.current) return;
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refresh: load };
};

export default useAnalyticsFetch;
